import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { useConfirm, useAlert } from '../../components/dialogs/DialogProvider';
import type { ApiError } from '../../api/client';
import type { FirmwareSkewBucket } from '../../api/types';
import {
  FIRMWARE_ROLLOUT_TARGET,
  useFirmwareSkew,
  useSetFirmwareRolloutPaused,
} from '../../api/queries';

/** Platform-only gate for the rollout pause/resume control. */
export const FIRMWARE_PROMOTE_PERMISSION = 'provisioning.releases.promote';

/**
 * ESP32 firmware OTA (M8) — fleet firmware-skew card + rollout pause/resume.
 *
 * The skew card shows one bar per distinct running firmware version (plus a
 * "never reported" bucket for HATs that have not yet uplinked their version),
 * so an operator/ARMS-Staff user can see at a glance how fragmented the fleet
 * is mid-rollout. The pause/resume control (ARMS-Staff,
 * provisioning.releases.promote) halts/resumes the esp32-firmware rollout; the
 * resulting state is surfaced loudly as a chip.
 *
 * `canPromote` is decided by the caller from useMyPermissions so the control is
 * hidden — not merely disabled — for non-holders (PORTAL_FORM_RULES §11/§12).
 */
export function FirmwareSkewPanel({ canPromote }: { canPromote: boolean }) {
  const skew = useFirmwareSkew();
  const setPaused = useSetFirmwareRolloutPaused();
  const confirm = useConfirm();
  const alert = useAlert();

  // No GET for the current pause state in the M8 contract — we learn it from
  // the PUT response. Until the operator acts (or a future status read lands)
  // it is genuinely unknown, so we offer both actions and only show the state
  // chip once a mutation has reported back.
  const [paused, setPausedState] = useState<boolean | null>(null);

  const buckets = useMemo<FirmwareSkewBucket[]>(
    () => (skew.data?.buckets ?? [])
      .slice()
      // Highest count first; the never-reported bucket sinks to the bottom.
      .sort((a, b) => b.count - a.count),
    [skew.data?.buckets],
  );
  const total = skew.data?.total ?? 0;

  const onError = useCallback((title: string, e: unknown) => {
    const err = e as unknown as ApiError;
    const msg = err?.status === 404
      ? 'The firmware-rollout endpoint is not available yet. Try again after the next deploy.'
      : err?.message ?? 'The action did not complete.';
    alert({ title, message: msg, severity: 'error' });
  }, [alert]);

  const setRollout = useCallback(async (next: boolean) => {
    const ok = await confirm({
      title: next ? 'Pause firmware rollout' : 'Resume firmware rollout',
      message: next
        ? 'Pause the esp32-firmware OTA rollout. In-service HATs stop being offered the new firmware on their next poll; HATs mid-update are unaffected. Resume re-enables the rollout fleet-wide.'
        : 'Resume the esp32-firmware OTA rollout. Eligible HATs are offered the new firmware again on their next poll.',
      confirmLabel: next ? 'Pause rollout' : 'Resume rollout',
      destructive: next,
    });
    if (!ok) return;
    setPaused.mutate(
      { paused: next, target: FIRMWARE_ROLLOUT_TARGET },
      {
        onSuccess: (res) => setPausedState(res.paused),
        onError: (e) => onError(next ? 'Pause failed' : 'Resume failed', e),
      },
    );
  }, [confirm, setPaused, onError]);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
      >
        <Box>
          <Typography variant="h6" gutterBottom>
            Firmware skew{' '}
            <Chip size="small" label={`${total} HAT${total === 1 ? '' : 's'}`} />
            {paused !== null && (
              <Chip
                size="small"
                sx={{ ml: 1 }}
                color={paused ? 'warning' : 'success'}
                label={paused ? 'rollout paused' : 'rollout running'}
                data-testid="firmware-rollout-state"
              />
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Distinct running firmware versions across the ESP32-P4 HAT fleet, as
            last reported by each site controller. A wide spread means a rollout
            is still in flight.
          </Typography>
        </Box>

        {canPromote && (
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              startIcon={<PauseCircleOutlineIcon />}
              onClick={() => void setRollout(true)}
              disabled={setPaused.isPending}
              data-testid="firmware-rollout-pause"
            >
              Pause rollout
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="success"
              startIcon={<PlayCircleOutlineIcon />}
              onClick={() => void setRollout(false)}
              disabled={setPaused.isPending}
              data-testid="firmware-rollout-resume"
            >
              Resume rollout
            </Button>
          </Stack>
        )}
      </Stack>

      <Box sx={{ mt: 2 }}>
        {skew.isLoading ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">Loading skew…</Typography>
          </Stack>
        ) : buckets.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No HATs have reported firmware yet.
          </Typography>
        ) : (
          <Stack spacing={1.25}>
            {buckets.map((b) => {
              const pct = total > 0 ? Math.round((b.count / total) * 100) : 0;
              const neverReported = b.firmwareVersion === null;
              return (
                <Box key={b.firmwareVersion ?? '__never__'}>
                  <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                    <Typography variant="body2" component="span">
                      {neverReported ? (
                        <Typography variant="body2" component="span" color="text.secondary">
                          — (never reported)
                        </Typography>
                      ) : (
                        <code>{b.firmwareVersion}</code>
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {b.count} ({pct}%)
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={pct}
                    color={neverReported ? 'inherit' : 'primary'}
                    sx={{ height: 6, borderRadius: 1, mt: 0.25 }}
                  />
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    </Paper>
  );
}
